import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckCircle2, ShieldCheck, Quote, FileText, Wrench, Building2, DollarSign, Lock, Zap, CreditCard, ChevronDown, ChevronUp, Star, Check, AlertCircle, Plus, Minus, ZoomIn, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
type Tomo = {
  num: number;
  title: string;
  subtitle: string;
  body: string;
  tags: string[];
  image?: string;
};
const TOMOS: Tomo[] = [{
  num: 1,
  title: 'Diagnóstico Avanzado de Fallas',
  subtitle: 'La base técnica que nadie te enseña en el oficio',
  body: 'Identificá cualquier falla en calefones, calefactores, termotanques y calderas con criterio técnico. Dejá de adivinar. Llegá a cada trabajo sabiendo qué hacer.',
  tags: ['Diagnóstico preciso', 'Manómetro', 'Presión', 'Fallas comunes'],
  image: 'https://cdn.magicpatterns.com/uploads/bDGTNjV1qzw98VSRaQ2CEQ/ChatGPT_Image_23_jun_2026,_02_54_03_p.m..png'
}, {
  num: 2,
  title: 'Instalaciones que Otros Rechazan',
  subtitle: 'Los trabajos complejos que te hacen ganar el doble',
  body: 'Tiro balanceado en espacios reducidos, conexiones múltiples, instalaciones en edificios y PH. Todo lo que otros gasistas no pueden hacer.',
  tags: ['Tiro balanceado', 'Espacios reducidos', 'Ticket alto'],
  image: 'https://cdn.magicpatterns.com/uploads/kuL5znxv8CHTXhVDfSAJfa/Tomo_2.png'
}, {
  num: 3,
  title: 'Normativa y Documentación Profesional',
  subtitle: 'Nunca más expuesto ante una inspección',
  body: 'Formulario 3.5, habilitaciones, rehabilitaciones con Metrogas, Ecogas y Camuzzi. Todo explicado paso a paso.',
  tags: ['Formulario 3.5', 'ENARGAS', 'Metrogas', 'Habilitaciones'],
  image: 'https://cdn.magicpatterns.com/uploads/7WrRLazNaEdCgdh8Bua83N/Tomo_3.png'
}, {
  num: 4,
  title: 'El Precio Justo',
  subtitle: 'Cobrá lo que vale tu trabajo desde el próximo presupuesto',
  body: 'Fórmula exacta para presupuestar sin quedarte corto ni espantar al cliente. Calculadora de costos incluida.',
  tags: ['Precios', 'Fórmula', 'Calculadora', 'Margen real'],
  image: 'https://cdn.magicpatterns.com/uploads/xz9B21DTdUPegQKUz4QaP8/tOMO4.png'
}, {
  num: 5,
  title: 'El Teléfono que No Para',
  subtitle: 'Sistema para que los clientes te llamen y te recomienden',
  body: 'Cómo conseguir clientes nuevos, reactivar los que ya te conocen y construir una cartera estable.',
  tags: ['Clientes', 'Google', 'Redes', 'Referidos'],
  image: 'https://cdn.magicpatterns.com/uploads/hyh5nE65kqGQ8XQRTNiwkr/tomo5.png'
}, {
  num: 6,
  title: 'De Gasista a Empresa',
  subtitle: 'La plata, el ayudante y la reputación que te hacen crecer',
  body: 'Sistema de los 4 sobres para manejar el dinero, cómo sumar un ayudante y cómo entrar al mercado corporativo.',
  tags: ['Dinero', 'Ayudante', 'Edificios', 'Inmobiliarias'],
  image: 'https://cdn.magicpatterns.com/uploads/eUgvR1EqzHT23uVbTfqJW3/TOMO6.png'
}];
const HERO_PILLS = ['Tomo 1 · Diagnóstico', 'Tomo 2 · Instalaciones', 'Tomo 3 · Normativa', 'Tomo 4 · Precios', 'Tomo 5 · Clientes', 'Tomo 6 · Empresa', 'Bono 1 · Carpeta', 'Bono 2 · Anti-Regateo', 'Bono 3 · Mes Flojo'];
const HERO_IMAGE_SOLO = 'https://cdn.magicpatterns.com/uploads/dencSdXRKW79RgoFNMuiQR/ChatGPT_Image_23_jun_2026,_04_35_10_p.m..png';
const HERO_IMAGE_SPREAD = 'https://cdn.magicpatterns.com/uploads/ka14GaYafjc3HDkSS4CZqQ/ChatGPT_Image_23_jun_2026,_05_05_46_p.m..png';
function HeroReveal() {
  const [revealed, setRevealed] = useState(false);
  return <div className="relative flex flex-col items-center text-center w-full my-8" onMouseEnter={() => setRevealed(true)} onMouseLeave={() => setRevealed(false)} data-id="element-16">
      {/* Orange glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-2/3 h-2/3 bg-[#F4820A] blur-[120px] opacity-20 rounded-full pointer-events-none" data-id="element-17" />

      {/* Top label for spread (state 2) */}
      <div className={`relative z-10 transition-all duration-500 ${revealed ? 'opacity-100 h-auto mb-3' : 'opacity-0 h-0 overflow-hidden'}`} data-id="element-18">
        <p className="text-[#F4820A] text-xs sm:text-sm font-bold uppercase tracking-widest" data-id="element-19">
          6 Tomos + 3 Bonos — Todo lo que necesitás
        </p>
      </div>

      {/* Clickable/Hoverable image */}
      <button type="button" onClick={() => setRevealed(!revealed)} aria-label={revealed ? 'Kit completo: 6 tomos y 3 bonos' : 'Pasá el cursor para ver todo lo que incluye el kit'} className={`relative z-10 focus:outline-none transition-all duration-[400ms] ease-in-out ${revealed ? 'cursor-default' : 'cursor-pointer hover:scale-105'}`} style={{
      width: revealed ? 'min(406px, 100%)' : 'min(266px, 90%)'
    }} data-id="element-20">
        <img src={revealed ? HERO_IMAGE_SPREAD : HERO_IMAGE_SOLO} alt={revealed ? 'Kit Gasista Profesional completo — 6 tomos y 3 bonos' : 'Kit Gasista Profesional'} loading="eager" fetchPriority="high" decoding="async" width={406} height={520} className="w-full h-auto object-contain drop-shadow-2xl transition-all duration-[400ms] ease-in-out" data-id="element-21" />
      </button>

      <div className="relative z-10 mt-4 h-6" data-id="element-22">
        <p className={`text-[#F4820A] text-[13px] font-bold uppercase tracking-widest transition-opacity duration-300 ${revealed ? 'opacity-0' : 'opacity-100 animate-pulse'}`} data-id="element-23">
          Pasá el cursor o tocá el kit para ver todo{' '}
          <span className="inline-block animate-bounce" data-id="element-24">↓</span>
        </p>
      </div>
    </div>;
}
function TomoThumb({
  tomo
}: {
  tomo: Tomo;
}) {
  if (tomo.image) {
    return <img src={tomo.image} alt={`Tomo ${tomo.num} — ${tomo.title}`} loading="lazy" decoding="async" className="w-full h-full object-contain bg-[#1B2A4A] p-1" data-id="element-25" />;
  }
  return <>
      <div className="w-full h-full bg-[#1B2A4A] flex flex-col items-center justify-center text-white" data-id="element-26">
        <span className="text-[10px] font-bold tracking-widest text-[#F4820A]" data-id="element-27">
          TOMO
        </span>
        <span className="text-3xl font-black leading-none" data-id="element-28">{tomo.num}</span>
      </div>
    </>;
}
function TomoCard({
  tomo,
  isOpen,
  onToggle,
  onZoom
}: {
  tomo: Tomo;
  isOpen: boolean;
  onToggle: () => void;
  onZoom: () => void;
}) {
  return <>
      <div className={`bg-white rounded-xl overflow-hidden transition-shadow border-l-4 ${isOpen ? 'border-[#F4820A] shadow-lg' : 'border-[#1B2A4A] shadow-sm hover:shadow-md'}`} data-id="element-29">
        <div className="flex items-stretch gap-4 p-4" data-id="element-30">
          <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-label={`${isOpen ? 'Cerrar' : 'Ver'} detalle del Tomo ${tomo.num}: ${tomo.title}`} className="group relative w-20 sm:w-24 shrink-0 rounded-lg overflow-hidden ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F4820A] aspect-[2/3]" data-id="element-31">
            <TomoThumb tomo={tomo} data-id="element-32" />
            <span className="absolute top-1 left-1 bg-[#F4820A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow" data-id="element-33">
              T{tomo.num}
            </span>
            <span className="absolute inset-0 bg-[#1B2A4A]/0 group-hover:bg-[#1B2A4A]/40 transition-colors flex items-center justify-center" data-id="element-34">
              <ZoomIn size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-35" />
            </span>
          </button>

          <div className="flex-1 min-w-0" data-id="element-36">
            <div className="flex items-start justify-between gap-3" data-id="element-37">
              <button type="button" onClick={onToggle} className="text-left focus:outline-none" data-id="element-38">
                <h3 className="font-bold text-lg md:text-xl text-[#1B2A4A] leading-tight" data-id="element-39">
                  {tomo.title}
                </h3>
                <p className="text-[#F4820A] font-medium text-sm mt-1" data-id="element-40">
                  {tomo.subtitle}
                </p>
              </button>
              <button type="button" onClick={onToggle} aria-label={isOpen ? 'Cerrar detalle' : 'Ver detalle'} className="shrink-0 w-8 h-8 rounded-full bg-[#1B2A4A]/5 hover:bg-[#F4820A] hover:text-white text-[#1B2A4A] flex items-center justify-center transition-colors" data-id="element-41">
                {isOpen ? <Minus size={16} data-id="element-42" /> : <Plus size={16} data-id="element-43" />}
              </button>
            </div>

            {isOpen && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-44">
                <p className="text-gray-600 mb-4" data-id="element-45">{tomo.body}</p>
                <div className="flex flex-wrap gap-2 mb-4" data-id="element-46">
                  {tomo.tags.map(tag => <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded" data-id="element-47">
                      {tag}
                    </span>)}
                </div>
                {tomo.image && <button type="button" onClick={onZoom} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F4820A] hover:underline focus:outline-none" data-id="element-48">
                    <ZoomIn size={16} data-id="element-49" /> Ver portada del Tomo {tomo.num}
                  </button>}
              </div>}
          </div>
        </div>
      </div>
    </>;
}
function TomoLightbox({
  tomo,
  onClose
}: {
  tomo: Tomo;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return <>
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`Portada del Tomo ${tomo.num}`} onClick={onClose} data-id="element-50">
        <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-id="element-51">
          <X size={22} data-id="element-52" />
        </button>
        <img src={tomo.image} alt={`Tomo ${tomo.num} — ${tomo.title}`} loading="lazy" decoding="async" className="max-h-[88vh] max-w-full rounded-xl shadow-2xl object-contain" onClick={e => e.stopPropagation()} data-id="element-53" />
      </div>
    </>;
}
const COUNTDOWN_HOURS = 23;
const COUNTDOWN_MINUTES = 45;
const COUNTDOWN_SECONDS = 0;
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: COUNTDOWN_HOURS,
    minutes: COUNTDOWN_MINUTES,
    seconds: COUNTDOWN_SECONDS
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.hours === 0 && prev.minutes === 0 && prev.seconds === 0) {
          clearInterval(timer);
          return prev;
        }
        let newSec = prev.seconds - 1;
        let newMin = prev.minutes;
        let newHour = prev.hours;
        if (newSec < 0) {
          newSec = 59;
          newMin -= 1;
        }
        if (newMin < 0) {
          newMin = 59;
          newHour -= 1;
        }
        return {
          hours: newHour,
          minutes: newMin,
          seconds: newSec
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <>
      <div className="flex items-center justify-center space-x-3 text-3xl md:text-4xl font-bold text-[#F4820A] tabular-nums" data-id="element-54">
        <div className="flex flex-col items-center" data-id="element-55">
          <div className="bg-[#111] border border-[#F4820A]/30 rounded-lg w-16 h-16 md:w-20 md:h-20 flex items-center justify-center shadow-[0_0_15px_rgba(244,130,10,0.1)]" data-id="element-56">
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <span className="text-[10px] text-gray-500 font-medium mt-2 tracking-widest uppercase" data-id="element-57">
            Horas
          </span>
        </div>
        <span className="text-gray-600 pb-6" data-id="element-58">:</span>
        <div className="flex flex-col items-center" data-id="element-59">
          <div className="bg-[#111] border border-[#F4820A]/30 rounded-lg w-16 h-16 md:w-20 md:h-20 flex items-center justify-center shadow-[0_0_15px_rgba(244,130,10,0.1)]" data-id="element-60">
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <span className="text-[10px] text-gray-500 font-medium mt-2 tracking-widest uppercase" data-id="element-61">
            Min
          </span>
        </div>
        <span className="text-gray-600 pb-6" data-id="element-62">:</span>
        <div className="flex flex-col items-center" data-id="element-63">
          <div className="bg-[#111] border border-[#F4820A]/30 rounded-lg w-16 h-16 md:w-20 md:h-20 flex items-center justify-center shadow-[0_0_15px_rgba(244,130,10,0.1)]" data-id="element-64">
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <span className="text-[10px] text-gray-500 font-medium mt-2 tracking-widest uppercase" data-id="element-65">
            Seg
          </span>
        </div>
      </div>
    </>;
}
function AccordionItem({
  title,
  children,
  defaultOpen = false
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <>
      <div className="border border-gray-200 rounded-xl mb-4 bg-white overflow-hidden" data-id="element-66">
        <button className="w-full flex items-center justify-between p-5 text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-67">
          <span className="font-bold text-[#2D2D2D] text-lg" data-id="element-68">{title}</span>
          {isOpen ? <ChevronUp className="text-[#F4820A]" data-id="element-69" /> : <ChevronDown className="text-gray-400" data-id="element-70" />}
        </button>
        {isOpen && <div className="p-5 pt-0 text-gray-600 border-t border-gray-100 mt-2" data-id="element-71">
            {children}
          </div>}
      </div>
    </>;
}
function PainPointAccordion({
  title,
  desc,
  defaultOpen = false
}: {
  title: string;
  desc: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <>
      <div className="bg-white/5 border-l-4 border-[#C62828] rounded-r-lg mb-3 overflow-hidden transition-colors hover:bg-white/10" data-id="element-72">
        <button className="w-full flex items-center justify-between p-4 md:p-5 text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-73">
          <span className="text-white font-bold text-base md:text-lg pr-4" data-id="element-74">
            {title}
          </span>
          {isOpen ? <ChevronUp className="text-[#C62828] shrink-0" data-id="element-75" /> : <ChevronDown className="text-gray-400 shrink-0" data-id="element-76" />}
        </button>
        {isOpen && <div className="px-4 md:px-5 pb-4 md:pb-5 pt-0 text-gray-400 text-sm md:text-base border-t border-white/5 mt-2" data-id="element-77">
            — {desc}
          </div>}
      </div>
    </>;
}
export function LandingPage() {
  const [openTomo, setOpenTomo] = useState<number | null>(null);
  const [zoomTomo, setZoomTomo] = useState<Tomo | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'tecnico' | 'completo'>('completo');
  const testimonialsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Inyección manual segura del Meta Pixel para evitar errores de DOM
    const initPixel = () => {
      // @ts-ignore
      if (window.fbq) return;
      // @ts-ignore
      const n = window.fbq = function () {
        // @ts-ignore
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      // @ts-ignore
      if (!window._fbq) window._fbq = n;
      // @ts-ignore
      n.push = n;
      // @ts-ignore
      n.loaded = !0;
      // @ts-ignore
      n.version = '2.0';
      // @ts-ignore
      n.queue = [];
      const t = document.createElement('script');
      t.async = !0;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        document.head.appendChild(t);
      }
    };
    initPixel();
    // @ts-ignore
    window.fbq('init', '864611220016529');
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const checkoutLink = selectedPlan === 'completo' ? 'https://checkout.ideahubdigital.online/cart/48901548769499:1' : 'https://checkout.ideahubdigital.online/cart/48901548802267:1';
  const scrollTestimonials = (direction: 'left' | 'right') => {
    if (testimonialsRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      testimonialsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const waTestimonialsRef = useRef<HTMLDivElement>(null);
  const scrollWaTestimonials = (direction: 'left' | 'right') => {
    if (waTestimonialsRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      waTestimonialsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return <>
      <PurchasePopup data-id="element-78" />
      <div className="min-h-screen bg-[#FAFAFA] font-sans text-[#2D2D2D]" data-id="element-79">
        {/* SECTION 2 — HERO */}
        <section className="bg-[#1B2A4A] text-white pt-16 pb-20 px-4 relative overflow-hidden" data-id="element-80">
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#F4820A 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-81"></div>
          <div className="max-w-4xl mx-auto flex flex-col items-center text-center relative z-10" data-id="element-82">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-4" data-id="element-83">
              Cobrás lo mismo hace años.
              <br data-id="element-84" />
              <span className="text-[#F4820A]" data-id="element-85">Eso cambia hoy.</span>
            </h1>

            <HeroReveal data-id="element-86" />

            <p className="text-gray-300 text-lg md:text-xl max-w-2xl mt-4 mb-8" data-id="element-87">
              El sistema completo para gasistas matriculados que quieren cobrar
              más, tomar mejores trabajos y crecer — sin depender de que el
              teléfono suene solo.
            </p>

            <div className="flex flex-col items-center gap-1 mb-8" data-id="element-88">
              <div className="flex items-center gap-1 text-[#F4820A]" data-id="element-89">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} size={18} className="fill-[#F4820A]" data-id="element-90" />)}
                <span className="ml-1 text-sm font-bold" data-id="element-91">4.9/5</span>
              </div>
              <p className="text-[#78909c] text-xs" data-id="element-92">
                Más de 258 gasistas matriculados ya lo usan
              </p>
            </div>

            <a href="#pricing" className="inline-block w-full md:w-auto bg-[#F4820A] hover:bg-[#d97409] text-white font-bold text-lg py-4 px-12 rounded-lg transition-colors shadow-lg mb-4" data-id="element-93">
              QUIERO MI KIT AHORA ↓
            </a>
            <p className="text-[#78909c] text-xs mb-12" data-id="element-94">
              Descarga inmediata · Garantía 7 días · Acceso de por vida
            </p>

            <div className="flex flex-wrap justify-center items-center gap-8 pt-8 border-t border-white/10 w-full max-w-2xl" data-id="element-95">
              <div className="text-center" data-id="element-96">
                <div className="font-bold text-xl" data-id="element-97">+258</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-98">
                  Gasistas
                </div>
              </div>
              <div className="text-center" data-id="element-99">
                <div className="font-bold text-xl flex items-center justify-center gap-1" data-id="element-100">
                  4.9{' '}
                  <Star size={16} className="fill-[#F4820A] text-[#F4820A]" data-id="element-101" />
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-102">
                  Rating
                </div>
              </div>
              <div className="text-center" data-id="element-103">
                <div className="font-bold text-xl" data-id="element-104">Todo</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-105">
                  Argentina
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <div className="bg-[#132035] py-4 px-4" data-id="element-106">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-white text-sm font-medium text-center" data-id="element-107">
            <span className="flex items-center gap-1.5" data-id="element-108">
              <CheckCircle2 size={16} className="text-[#F4820A]" data-id="element-109" /> PDF Digital
            </span>
            <span className="text-[#F4820A]" data-id="element-110">·</span>
            <span className="flex items-center gap-1.5" data-id="element-111">
              <Zap size={16} className="text-[#F4820A]" data-id="element-112" /> Acceso Inmediato
            </span>
            <span className="text-[#F4820A]" data-id="element-113">·</span>
            <span className="flex items-center gap-1.5" data-id="element-114">
              <Lock size={16} className="text-[#F4820A]" data-id="element-115" /> Garantía 7 días
            </span>
            <span className="text-[#F4820A]" data-id="element-116">·</span>
            <span className="flex items-center gap-1.5" data-id="element-117">
              <CreditCard size={16} className="text-[#F4820A]" data-id="element-118" /> Mercado Pago
            </span>
          </div>
        </div>

        {/* SECTION 4 — PAIN POINTS */}
        <section className="bg-[#1B2A4A] py-10 md:py-20 px-4" data-id="element-119">
          <div className="max-w-3xl mx-auto" data-id="element-120">
            <div className="text-center mb-8 md:mb-12" data-id="element-121">
              <h2 className="text-[#F4820A] font-bold text-2xl md:text-4xl mb-2 md:mb-4" data-id="element-122">
                ¿TE PASA ESTO?
              </h2>
              <p className="text-white text-base md:text-lg" data-id="element-123">
                Si tenés matrícula ENARGAS y trabajás en Argentina, esto te
                suena familiar:
              </p>
            </div>

            <div className="flex flex-col" data-id="element-124">
              {[{
              title: 'Llegás a un trabajo sin saber si la presión es correcta',
              desc: 'Sin criterio de diagnóstico, adivinás y rezás.'
            }, {
              title: 'Evitás trabajos complejos porque no te animás',
              desc: 'Tiro balanceado, instalaciones múltiples, trabajos en edificios.'
            }, {
              title: 'No sabés exactamente qué documentación presentar',
              desc: 'El Formulario 3.5, la habilitación, la rehabilitación con Metrogas.'
            }, {
              title: 'Cotizás de memoria y terminás cobrando de menos',
              desc: 'Sin fórmula de precios, siempre perdés plata sin darte cuenta.'
            }, {
              title: 'Los clientes te regateán y no sabés qué decir',
              desc: '"Estás caro", "el otro me cobra menos", "¿no me hacés precio?"'
            }, {
              title: 'Los meses flojos te destruyen',
              desc: 'Enero, julio, cualquier mes de crisis. Sin plan, sin salida.'
            }, {
              title: 'No podés crecer más allá de trabajar solo',
              desc: 'Sin sistema, sin ayudante, sin clientes corporativos.'
            }, {
              title: 'Cobrás lo mismo hace años',
              desc: 'Sin dominar el negocio, tu techo ya está puesto.'
            }].map((pain, i) => <PainPointAccordion key={i} title={pain.title} desc={pain.desc} defaultOpen={i === 0} data-id="element-125" />)}
            </div>

            <div className="text-center mt-10 md:mt-16" data-id="element-126">
              <h3 className="text-xl md:text-3xl font-bold text-white" data-id="element-127">
                ¿Cuántos meses más vas a trabajar sin el sistema completo?
              </h3>
            </div>
          </div>
        </section>

        {/* SECTION 5 — FOR WHOM */}
        <section className="py-10 md:py-20 px-4 bg-[#1B2A4A]" data-id="element-128">
          <div className="max-w-3xl mx-auto" data-id="element-129">
            <div className="text-center mb-8 md:mb-12" data-id="element-130">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4 uppercase tracking-wide" data-id="element-131">
                ¿Para quién es el{' '}
                <span className="text-[#F4820A]" data-id="element-132">Kit Gasista Profesional?</span>
              </h2>
              <p className="text-gray-300 text-base md:text-lg" data-id="element-133">
                Diseñado para vos si querés cobrar más y trabajar mejor
              </p>
            </div>

            <div className="flex flex-col gap-4 md:gap-6 mb-8" data-id="element-134">
              <div className="bg-white rounded-2xl overflow-hidden flex shadow-lg" data-id="element-135">
                <div className="w-1/3 shrink-0" data-id="element-136">
                  <img src="https://cdn.magicpatterns.com/uploads/uVmpyT7nuSrQctdXBX5m1k/gasis_estancado.png" alt="Gasistas con experiencia estancados" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-137" />
                </div>
                <div className="p-4 md:p-6 flex flex-col justify-center w-2/3" data-id="element-138">
                  <p className="text-[#2D2D2D] text-sm md:text-base font-medium leading-snug" data-id="element-139">
                    Tenés años de oficio pero seguís cobrando lo mismo.{' '}
                    <span className="text-[#F4820A] font-bold" data-id="element-140">
                      El kit te abre los trabajos mejor pagos.
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden flex shadow-lg" data-id="element-141">
                <div className="w-1/3 shrink-0" data-id="element-142">
                  <img src="https://cdn.magicpatterns.com/uploads/jBsEtEJESmeUTFi9ZUmJWK/gasista_documentacion.png" alt="Gasistas con dudas en documentación" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-143" />
                </div>
                <div className="p-4 md:p-6 flex flex-col justify-center w-2/3" data-id="element-144">
                  <p className="text-[#2D2D2D] text-sm md:text-base font-medium leading-snug" data-id="element-145">
                    No dominás el Formulario 3.5, las habilitaciones o las
                    rehabilitaciones.{' '}
                    <span className="text-[#F4820A] font-bold" data-id="element-146">
                      El Tomo 3 lo resuelve.
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden flex shadow-lg" data-id="element-147">
                <div className="w-1/3 shrink-0" data-id="element-148">
                  <img src="https://cdn.magicpatterns.com/uploads/dmR6Y7SEYG6rK3kK6tjVbR/gasista_coorporativo.png" alt="Gasistas que quieren clientes corporativos" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-149" />
                </div>
                <div className="p-4 md:p-6 flex flex-col justify-center w-2/3" data-id="element-150">
                  <p className="text-[#2D2D2D] text-sm md:text-base font-medium leading-snug" data-id="element-151">
                    Querés entrar a edificios, inmobiliarias y constructoras.{' '}
                    <span className="text-[#F4820A] font-bold" data-id="element-152">
                      El Tomo 6 te da el sistema.
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl overflow-hidden flex shadow-lg" data-id="element-153">
                <div className="w-1/3 shrink-0" data-id="element-154">
                  <img src="https://cdn.magicpatterns.com/uploads/9uA6MqJ2ipWLYq3uixazJu/gasista_cobra_menos.png" alt="Gasistas que siempre terminan cobrando de menos" loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-155" />
                </div>
                <div className="p-4 md:p-6 flex flex-col justify-center w-2/3" data-id="element-156">
                  <p className="text-[#2D2D2D] text-sm md:text-base font-medium leading-snug" data-id="element-157">
                    Presupuestás de memoria y el cliente te regatea siempre.{' '}
                    <span className="text-[#F4820A] font-bold" data-id="element-158">
                      Los Tomos 4 y el Bono 2 lo resuelven.
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-159">
              <a href="#guarantee" className="text-[#F4820A] font-medium hover:underline inline-flex items-center gap-1 text-sm md:text-base" data-id="element-160">
                ¿No estás seguro si es para vos? Tenés 7 días de garantía →
              </a>
            </div>
          </div>
        </section>

        {/* SECTION 6 — STATS BAR */}
        <section className="bg-[#1B2A4A] py-8 md:py-16 px-4 border-y border-white/10" data-id="element-161">
          <div className="max-w-6xl mx-auto text-center" data-id="element-162">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-white font-bold text-lg md:text-xl mb-4 md:mb-6" data-id="element-163">
              <span data-id="element-164">+240 páginas</span>
              <span className="text-white/30 hidden md:inline" data-id="element-165">·</span>
              <span data-id="element-166">9 productos</span>
              <span className="text-white/30 hidden md:inline" data-id="element-167">·</span>
              <span data-id="element-168">Normativa ENARGAS</span>
              <span className="text-white/30 hidden md:inline" data-id="element-169">·</span>
              <span data-id="element-170">Todo aplicable</span>
            </div>
            <p className="text-[#F4820A] text-base md:text-lg font-medium mb-6 md:mb-10" data-id="element-171">
              Sistema completo · 6 Tomos + 3 Bonos · Para gasistas matriculados
              · Todo resuelto.
            </p>

            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-10" data-id="element-172">
              {['Diagnóstico', 'Normativa', 'Precios', 'Clientes', 'Documentación', 'Habilitaciones', 'Formulario 3.5', 'Anti-regateo', 'Mes flojo', 'Crecimiento'].map(tag => <span key={tag} className="bg-[#F4820A]/20 text-[#F4820A] text-[10px] md:text-xs font-bold px-2 md:px-3 py-1 rounded-full border border-[#F4820A]/30" data-id="element-173">
                  {tag}
                </span>)}
            </div>

            <p className="text-white text-lg md:text-xl font-bold mb-4 md:mb-6" data-id="element-174">
              Aplicable desde hoy en cualquier trabajo en Argentina.
            </p>

            <div className="bg-white/5 rounded-xl p-4 md:p-6 inline-block w-full md:w-auto" data-id="element-175">
              <p className="text-gray-400 text-xs md:text-sm mb-2 md:mb-3 uppercase tracking-wider font-bold" data-id="element-176">
                Temas que dominás:
              </p>
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed" data-id="element-177">
                Calefones · Calefactores · Termotanques · Calderas · Tiro
                balanceado
                <br className="hidden md:block" data-id="element-178" />
                <span className="md:hidden" data-id="element-179"> · </span>
                Habilitaciones · Rehabilitaciones · Edificios · Inmobiliarias ·
                Precios · Clientes
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 7 — CONTENT DETAIL */}
        <section className="py-10 md:py-20 px-4 bg-[#FAFAFA]" data-id="element-180">
          <div className="max-w-4xl mx-auto" data-id="element-181">
            <div className="text-center mb-8 md:mb-12" data-id="element-182">
              <h2 className="text-2xl md:text-4xl font-bold text-[#1B2A4A] mb-2" data-id="element-183">
                CONTENIDO COMPLETO
              </h2>
              <p className="text-[#F4820A] font-bold text-lg md:text-xl mb-2 md:mb-4" data-id="element-184">
                LOS 6 TOMOS
              </p>
              <p className="text-gray-600 text-sm md:text-base" data-id="element-185">
                Tu hoja de ruta para dominar el oficio y el negocio del gas
              </p>
              <p className="text-gray-400 text-xs md:text-sm mt-1 md:mt-2" data-id="element-186">
                Tocá la portada de cada tomo para ver el detalle
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-187">
              {TOMOS.map(tomo => <TomoCard key={tomo.num} tomo={tomo} isOpen={openTomo === tomo.num} onToggle={() => setOpenTomo(cur => cur === tomo.num ? null : tomo.num)} onZoom={() => setZoomTomo(tomo)} data-id="element-188" />)}
            </div>
          </div>
        </section>

        {zoomTomo && <TomoLightbox tomo={zoomTomo} onClose={() => setZoomTomo(null)} data-id="element-189" />}

        {/* SECTION 8 — BONUSES */}
        <section className="bg-[#1B2A4A] py-10 md:py-20 px-4" data-id="element-190">
          <div className="max-w-6xl mx-auto" data-id="element-191">
            <div className="text-center mb-8 md:mb-12" data-id="element-192">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2" data-id="element-193">
                INCLUIDOS GRATIS
              </h2>
              <p className="text-[#F4820A] font-bold text-lg md:text-xl mb-2 md:mb-4" data-id="element-194">
                3 Bonos Profesionales
              </p>
              <p className="text-gray-400 text-sm md:text-base" data-id="element-195">
                Sin costo extra — incluidos en todas las ofertas
              </p>
            </div>

            <div className="flex md:grid md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-4 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0" data-id="element-196">
              {[{
              image: 'https://cdn.magicpatterns.com/uploads/b9LFarkHMemCFsDyrenBt1/Bono1.png',
              title: 'Carpeta del Gasista Profesional',
              desc: 'Presupuestos, órdenes de trabajo, remitos, checklist de seguridad y protocolo de entrega. Listo para usar desde mañana.',
              val: '$12.990 ARS'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/ugP7zJ9emggReLCdD28cFT/Bono2.png',
              title: 'Guión Anti-Regateo',
              desc: "Las 5 respuestas exactas para cuando el cliente dice 'estás caro', 'el otro cobra menos' o '¿no me hacés precio?'.",
              val: '$9.990 ARS'
            }, {
              image: 'https://cdn.magicpatterns.com/uploads/neNNktfYqUxzUecdEjz8bV/Bono_3.png',
              title: 'El Mes Flojo',
              desc: 'Plan de 7 días para reactivar el trabajo cuando el teléfono no suena. Plantillas de mensajes y acciones concretas incluidas.',
              val: '$8.990 ARS'
            }].map((bono, i) => <div key={i} className="bg-[#0F1B33] rounded-xl overflow-hidden flex flex-col shadow-lg border border-white/10 shrink-0 w-[85vw] md:w-auto snap-center" data-id="element-197">
                  {/* Gold top bar */}
                  <div className="bg-[#F4820A] text-[#1B2A4A] text-center text-xs md:text-sm font-extrabold uppercase tracking-wide py-2" data-id="element-198">
                    Hoy: Gratis
                  </div>
                  {/* Cover image */}
                  <div className="p-4" data-id="element-199">
                    <img src={bono.image} alt={bono.title} loading="lazy" decoding="async" className="w-full rounded-lg object-contain aspect-[3/4] bg-[#1B2A4A]" data-id="element-200" />
                  </div>
                  {/* Content */}
                  <div className="px-4 md:px-6 pb-4 md:pb-6 flex flex-col flex-1 text-center" data-id="element-201">
                    <h3 className="text-[#F4820A] font-bold text-base md:text-lg mb-2 md:mb-3 uppercase" data-id="element-202">
                      {bono.title}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm mb-4 md:mb-6 flex-1" data-id="element-203">
                      {bono.desc}
                    </p>
                    <p className="text-gray-400 text-xs md:text-sm mb-3 md:mb-4" data-id="element-204">
                      Valor:{' '}
                      <span className="line-through text-gray-500" data-id="element-205">
                        {bono.val}
                      </span>
                    </p>
                    <div className="w-full border-2 border-[#2E7D32] text-[#2E7D32] font-bold py-2 md:py-3 rounded-lg flex items-center justify-center gap-2 text-sm md:text-base" data-id="element-206">
                      <Check size={18} data-id="element-207" /> Incluido sin costo
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="text-center bg-white/10 py-3 md:py-4 px-2 rounded-lg border border-white/20" data-id="element-208">
              <p className="text-white font-bold tracking-wide text-sm md:text-base" data-id="element-209">
                VALOR TOTAL BONOS:{' '}
                <span className="underline decoration-2 underline-offset-2" data-id="element-210">
                  $31.970 ARS
                </span>{' '}
                — <span className="text-[#2E7D32]" data-id="element-211">INCLUIDOS GRATIS</span>
              </p>
            </div>

            {/* Video Preview */}
            <div className="mt-8 md:mt-12 max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black" data-id="element-212">
              <video src="https://cdn.magicpatterns.com/uploads/4DNHDXjPxPdmPonpBoX1yi/Grabacio%CC%81n%20de%20pantalla%202026-06-24%20a%20la(s)%2013.14.08.mov" controls autoPlay muted loop playsInline className="w-full h-auto" data-id="element-213" />
            </div>
          </div>
        </section>

        {/* SECTION 9 — REALITY CHECK */}
        <section className="py-10 md:py-20 px-4 bg-[#FAFAFA]" data-id="element-214">
          <div className="max-w-5xl mx-auto" data-id="element-215">
            <div className="text-center mb-8 md:mb-12" data-id="element-216">
              <h2 className="text-2xl md:text-4xl font-bold text-[#1B2A4A] mb-2" data-id="element-217">
                La realidad
              </h2>
              <p className="text-gray-600 text-base md:text-lg" data-id="element-218">
                ¿Cuánto cuesta NO tenerlo?
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200" data-id="element-219">
              <div className="grid grid-cols-2 bg-gray-50 border-b border-gray-200" data-id="element-220">
                <div className="p-3 md:p-6 text-center border-r border-gray-200 flex items-center justify-center" data-id="element-221">
                  <h3 className="font-bold text-[#C62828] text-sm md:text-lg" data-id="element-222">
                    SIN EL KIT
                  </h3>
                </div>
                <div className="p-3 md:p-6 text-center bg-[#2E7D32]/10 flex items-center justify-center" data-id="element-223">
                  <h3 className="font-bold text-[#2E7D32] text-sm md:text-lg" data-id="element-224">
                    CON EL KIT
                  </h3>
                </div>
              </div>

              {[['Rechazás trabajos complejos por inseguridad', 'Aceptás cualquier instalación con criterio técnico'], ['Cada presupuesto es una adivinanza', 'Fórmula de precios aplicada desde el primer día'], ['El cliente te regatea y vos cedés', 'Guión listo para defender tu precio siempre'], ['Los meses flojos te destruyen', 'Plan de 7 días para reactivar el trabajo'], ['Seguís cobrando lo mismo hace años', 'Sistema para crecer a edificios y corporativos'], ['Trabajás solo sin poder escalar', 'Cómo sumar un ayudante y multiplicar tu capacidad'], ['Documentación que genera ansiedad', 'Formularios y normativa ENARGAS dominados'], ['Cero estructura para el negocio', 'Sistema completo de ingresos y crecimiento']].map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-gray-100 last:border-0" data-id="element-225">
                  <div className="p-3 md:p-6 border-r border-gray-100 flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left" data-id="element-226">
                    <div className="md:mt-1 text-[#C62828] shrink-0" data-id="element-227">
                      <AlertCircle size={16} className="md:w-[18px] md:h-[18px]" data-id="element-228" />
                    </div>
                    <p className="text-gray-600 text-xs md:text-base" data-id="element-229">
                      {row[0]}
                    </p>
                  </div>
                  <div className="p-3 md:p-6 bg-[#2E7D32]/5 flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-3 text-center md:text-left" data-id="element-230">
                    <div className="md:mt-1 text-[#2E7D32] shrink-0" data-id="element-231">
                      <CheckCircle2 size={16} className="md:w-[18px] md:h-[18px]" data-id="element-232" />
                    </div>
                    <p className="text-[#1B2A4A] font-medium text-xs md:text-base" data-id="element-233">
                      {row[1]}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* SECTION 9b — TESTIMONIALS */}
        <section className="py-10 md:py-20 px-4 bg-[#1B2A4A]" data-id="element-234">
          <div className="max-w-5xl mx-auto" data-id="element-235">
            <div className="text-center mb-8 md:mb-12" data-id="element-236">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2" data-id="element-237">
                LO QUE DICEN LOS GASISTAS
              </h2>
              <p className="text-gray-300 text-base md:text-lg" data-id="element-238">
                Matriculados de toda Argentina que ya lo están aplicando
              </p>
            </div>

            <div ref={testimonialsRef} className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-4 -mx-4 px-4" data-id="element-239">
              {[{
              name: 'Carlos Medina',
              city: 'Córdoba',
              photo: 'https://randomuser.me/api/portraits/men/32.jpg',
              quote: 'En el primer mes apliqué la fórmula de precios y dejé de regalar trabajo. Recuperé la inversión con un solo presupuesto.'
            }, {
              name: 'Diego Rossi',
              city: 'Rosario',
              photo: 'https://randomuser.me/api/portraits/men/52.jpg',
              quote: 'El Tomo 3 me sacó la ansiedad de las habilitaciones. Ahora presento la documentación sin dudar y los clientes me ven más profesional.'
            }, {
              name: 'Martín Suárez',
              city: 'La Plata',
              photo: 'https://randomuser.me/api/portraits/men/76.jpg',
              quote: 'Empecé a tomar trabajos en edificios que antes rechazaba. El sistema de clientes me cambió la forma de laburar.'
            }].map((t, i) => <div key={i} className="bg-white rounded-2xl shadow-lg p-6 flex flex-col shrink-0 w-[85vw] sm:w-[340px] snap-center" data-id="element-240">
                  <div className="flex items-center gap-4 mb-4" data-id="element-241">
                    <img src={t.photo} alt={`${t.name}, gasista matriculado de ${t.city}`} loading="lazy" decoding="async" width={56} height={56} className="w-14 h-14 rounded-full object-cover ring-2 ring-[#F4820A]/30" data-id="element-242" />
                    <div data-id="element-243">
                      <p className="font-bold text-[#1B2A4A] leading-tight" data-id="element-244">
                        {t.name}
                      </p>
                      <p className="text-gray-500 text-sm" data-id="element-245">{t.city}</p>
                      <div className="flex items-center gap-0.5 mt-1 text-[#F4820A]" data-id="element-246">
                        {[1, 2, 3, 4, 5].map(s => <Star key={s} size={13} className="fill-[#F4820A]" data-id="element-247" />)}
                      </div>
                    </div>
                  </div>
                  <Quote size={22} className="text-[#F4820A]/30 mb-2 shrink-0" data-id="element-248" />
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed flex-1" data-id="element-249">
                    {t.quote}
                  </p>
                </div>)}
            </div>

            {/* Arrow controls for those who don't swipe */}
            <div className="flex justify-center gap-3 mt-4" data-id="element-250">
              <button type="button" onClick={() => scrollTestimonials('left')} aria-label="Ver testimonio anterior" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#F4820A] text-white flex items-center justify-center transition-colors" data-id="element-251">
                <ChevronLeft size={22} data-id="element-252" />
              </button>
              <button type="button" onClick={() => scrollTestimonials('right')} aria-label="Ver siguiente testimonio" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#F4820A] text-white flex items-center justify-center transition-colors" data-id="element-253">
                <ChevronRight size={22} data-id="element-254" />
              </button>
            </div>

            {/* WhatsApp screenshots */}
            <div className="text-center mt-12 md:mt-16 mb-6 md:mb-8" data-id="element-255">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-256">
                Mensajes reales que nos llegan
              </h3>
              <p className="text-gray-300 text-sm md:text-base" data-id="element-257">
                Capturas de gasistas que ya aplicaron el Kit
              </p>
            </div>

            <div ref={waTestimonialsRef} className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-4 -mx-4 px-4" data-id="element-258">
              {[{
              src: 'https://cdn.magicpatterns.com/uploads/abzCXTBVgZw1jpJiCoBQGN/Testimonio_1.png',
              name: 'Roberto S.'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/sANmduU82WSe8mhovw4e22/Testimonio_2.png',
              name: 'Diego F.'
            }, {
              src: 'https://cdn.magicpatterns.com/uploads/6ZBqxSyWPq79sUPAJ32cZH/Tersmonio_3_.png',
              name: 'Martín G.'
            }].map((shot, i) => <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden shrink-0 w-[80vw] sm:w-[320px] snap-center" data-id="element-259">
                  <img src={shot.src} alt={`Mensaje de WhatsApp de ${shot.name}, gasista matriculado`} loading="lazy" decoding="async" className="w-full h-auto object-contain" data-id="element-260" />
                </div>)}
            </div>

            {/* Arrow controls for WhatsApp screenshots */}
            <div className="flex justify-center gap-3 mt-4" data-id="element-261">
              <button type="button" onClick={() => scrollWaTestimonials('left')} aria-label="Ver captura anterior" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#F4820A] text-white flex items-center justify-center transition-colors" data-id="element-262">
                <ChevronLeft size={22} data-id="element-263" />
              </button>
              <button type="button" onClick={() => scrollWaTestimonials('right')} aria-label="Ver siguiente captura" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#F4820A] text-white flex items-center justify-center transition-colors" data-id="element-264">
                <ChevronRight size={22} data-id="element-265" />
              </button>
            </div>
          </div>
        </section>

        {/* SECTION 10 & 11 — UNIFIED PRICING & COUNTDOWN */}
        <section className="bg-[#111] py-16 md:py-24 px-4" id="pricing" data-id="element-266">
          <div className="max-w-xl mx-auto" data-id="element-267">
            {/* Header */}
            <div className="text-center mb-8" data-id="element-268">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 uppercase tracking-wide" data-id="element-269">
                NO DEJES PASAR ESTA OPORTUNIDAD
              </h2>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-270">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full" data-id="element-271">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-272"></div>
                  <span className="text-white text-xs font-bold tracking-wider" data-id="element-273">
                    OFERTA EN VIVO
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full" data-id="element-274">
                  <span className="text-white text-xs font-bold tracking-wider" data-id="element-275">
                    +$10.000 DESPUÉS
                  </span>
                </div>
              </div>

              <p className="text-gray-400 text-sm tracking-widest uppercase mb-4" data-id="element-276">
                TERMINA EN
              </p>

              <div className="mb-8" data-id="element-277">
                <CountdownTimer data-id="element-278" />
              </div>

              <div className="max-w-xs mx-auto mb-12" data-id="element-279">
                <div className="flex justify-between text-xs text-gray-400 font-bold mb-2 uppercase tracking-wider" data-id="element-280">
                  <span data-id="element-281">CUPOS RESTANTES</span>
                  <span className="text-[#F4820A]" data-id="element-282">68% TOMADOS</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-283">
                  <div className="h-full bg-[#F4820A] w-[68%] rounded-full" data-id="element-284"></div>
                </div>
              </div>

              <h3 className="text-xl md:text-2xl font-bold text-white mb-2 uppercase tracking-wide" data-id="element-285">
                ELEGÍ TU KIT GASISTA PROFESIONAL
              </h3>
              <p className="text-gray-400 text-sm md:text-base" data-id="element-286">
                Ambas opciones incluyen los 3 bonos completos
              </p>
            </div>

            {/* Pricing Options — Compact Side-by-Side */}
            <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-287">
              {/* Option 1: Técnico */}
              <button onClick={() => setSelectedPlan('tecnico')} className={`text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-300 flex flex-col h-full ${selectedPlan === 'tecnico' ? 'border-[#F4820A] bg-[#F4820A]/5' : 'border-white/10 bg-white/5 hover:bg-white/10'}`} data-id="element-288">
                <div className="flex items-center gap-2 mb-2" data-id="element-289">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'tecnico' ? 'border-[#F4820A]' : 'border-gray-500'}`} data-id="element-290">
                    {selectedPlan === 'tecnico' && <div className="w-2 h-2 rounded-full bg-[#F4820A]" data-id="element-291"></div>}
                  </div>
                  <FileText size={14} className={selectedPlan === 'tecnico' ? 'text-[#F4820A]' : 'text-gray-400'} data-id="element-292" />
                </div>
                <h4 className="text-white font-bold text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-293">
                  KIT TÉCNICO
                </h4>
                <p className="text-gray-400 text-[10px] md:text-xs mt-1 mb-2" data-id="element-294">
                  3 Tomos + 3 Bonos
                </p>
                <div className="mt-auto" data-id="element-295">
                  <span className="text-gray-500 line-through text-[10px] md:text-xs block mb-0.5" data-id="element-296">
                    $31.970
                  </span>
                  <span className="text-white font-bold text-base md:text-lg" data-id="element-297">
                    $19.990
                  </span>
                </div>
              </button>

              {/* Option 2: Completo */}
              <button onClick={() => setSelectedPlan('completo')} className={`text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-300 flex flex-col h-full relative ${selectedPlan === 'completo' ? 'border-[#F4820A] bg-[#F4820A]/5' : 'border-white/10 bg-white/5 hover:bg-white/10'}`} data-id="element-298">
                <div className="flex items-center gap-2 mb-2" data-id="element-299">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'completo' ? 'border-[#F4820A]' : 'border-gray-500'}`} data-id="element-300">
                    {selectedPlan === 'completo' && <div className="w-2 h-2 rounded-full bg-[#F4820A]" data-id="element-301"></div>}
                  </div>
                  <Star size={14} className={selectedPlan === 'completo' ? 'text-[#F4820A] fill-[#F4820A]' : 'text-gray-400'} data-id="element-302" />
                </div>
                <h4 className="text-white font-bold text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-303">
                  KIT COMPLETO
                </h4>
                <div className="flex flex-wrap items-center gap-1 mt-1 mb-2" data-id="element-304">
                  <span className="bg-[#F4820A] text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-305">
                    ⭐ MÁS ELEGIDO
                  </span>
                </div>
                <p className="text-gray-400 text-[10px] md:text-xs mb-2" data-id="element-306">
                  6 Tomos + 3 Bonos
                </p>
                <div className="mt-auto" data-id="element-307">
                  <span className="text-gray-500 line-through text-[10px] md:text-xs block mb-0.5" data-id="element-308">
                    $59.940
                  </span>
                  <span className="text-[#F4820A] font-bold text-lg md:text-xl" data-id="element-309">
                    $29.990
                  </span>
                </div>
              </button>
            </div>

            {/* CTA Button */}
            <a href={checkoutLink} className="w-full bg-[#F4820A] hover:bg-[#d97409] text-white font-bold text-lg md:text-xl py-3 md:py-4 rounded-xl transition-colors shadow-[0_0_30px_rgba(244,130,10,0.2)] mb-4 flex items-center justify-center gap-2" data-id="element-310">
              {selectedPlan === 'completo' ? 'SÍ, QUIERO EL KIT COMPLETO' : 'SÍ, QUIERO EL KIT TÉCNICO'}{' '}
              <span className="text-2xl leading-none" data-id="element-311">→</span>
            </a>

            <p className="text-gray-400 text-xs md:text-sm text-center mb-8 italic" data-id="element-312">
              La opción más elegida por gasistas que quieren el sistema completo
              de oficio y negocio
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-400 font-medium" data-id="element-313">
              <div className="flex items-center gap-1.5" data-id="element-314">
                <Lock size={16} className="text-[#F4820A]" data-id="element-315" /> Pago seguro
              </div>
              <div className="flex items-center gap-1.5" data-id="element-316">
                <Zap size={16} className="text-[#F4820A]" data-id="element-317" /> Acceso en 5 min
              </div>
              <div className="flex items-center gap-1.5" data-id="element-318">
                <ShieldCheck size={16} className="text-[#F4820A]" data-id="element-319" /> Garantía 7
                días
              </div>
              <div className="flex items-center gap-1.5" data-id="element-320">
                <CreditCard size={16} className="text-[#F4820A]" data-id="element-321" /> Mercado Pago
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 12 — GUARANTEE */}
        <section className="py-12 md:py-20 px-4 bg-[#FAFAFA]" id="guarantee" data-id="element-322">
          <div className="max-w-3xl mx-auto" data-id="element-323">
            <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-324">
              <div className="bg-[#2E7D32] py-3 px-4 text-center" data-id="element-325">
                <p className="text-white font-extrabold uppercase tracking-widest text-sm md:text-base" data-id="element-326">
                  Garantía total de 7 días
                </p>
              </div>
              <div className="p-6 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-8 text-center md:text-left" data-id="element-327">
                <div className="shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#2E7D32]/10 flex items-center justify-center" data-id="element-328">
                  <ShieldCheck size={56} className="text-[#2E7D32]" data-id="element-329" />
                </div>
                <div className="flex-1" data-id="element-330">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1B2A4A] mb-3" data-id="element-331">
                    El riesgo es{' '}
                    <span className="text-[#2E7D32]" data-id="element-332">todo nuestro</span>
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed" data-id="element-333">
                    Llevate el Kit Gasista Profesional, revisalo completo y
                    aplicalo en tus próximos trabajos. Si en{' '}
                    <span className="font-bold text-[#1B2A4A]" data-id="element-334">7 días</span> no
                    sentís que vale cada peso, te devolvemos el 100% de tu
                    dinero. Sin preguntas, sin vueltas. Escribís un mail y
                    listo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 13 — FAQ */}
        <section className="py-12 md:py-20 px-4 bg-white" id="faq" data-id="element-335">
          <div className="max-w-3xl mx-auto" data-id="element-336">
            <div className="text-center mb-8 md:mb-12" data-id="element-337">
              <h2 className="text-2xl md:text-4xl font-bold text-[#1B2A4A] mb-2" data-id="element-338">
                PREGUNTAS FRECUENTES
              </h2>
              <p className="text-gray-600 text-base md:text-lg" data-id="element-339">
                Todo lo que necesitás saber antes de empezar
              </p>
            </div>

            <div data-id="element-340">
              <AccordionItem title="¿Cómo recibo el Kit después de comprar?" defaultOpen data-id="element-341">
                Es 100% digital. Apenas se acredita el pago recibís un correo
                con el acceso para descargar todos los tomos y bonos en PDF. En
                menos de 5 minutos lo tenés en tu celular o computadora.
              </AccordionItem>
              <AccordionItem title="¿Necesito conocimientos previos?" data-id="element-342">
                Solo necesitás tu matrícula ENARGAS y ganas de cobrar mejor. El
                contenido está explicado paso a paso, desde el diagnóstico
                técnico hasta cómo presupuestar y conseguir clientes.
              </AccordionItem>
              <AccordionItem title="¿Sirve en toda Argentina?" data-id="element-343">
                Sí. La normativa y los formularios cubren las distribuidoras
                principales (Metrogas, Ecogas, Camuzzi) y la metodología de
                precios y clientes aplica en cualquier provincia del país.
              </AccordionItem>
              <AccordionItem title="¿Cómo puedo pagar?" data-id="element-344">
                Pagás de forma segura con Mercado Pago: tarjeta de crédito,
                débito o dinero en cuenta. El Kit Completo se puede abonar hasta
                en 3 cuotas sin interés.
              </AccordionItem>
              <AccordionItem title="¿Qué pasa si no me sirve?" data-id="element-345">
                Tenés 7 días de garantía total. Si no te convence, escribís un
                mail y te devolvemos el 100% de tu dinero. El riesgo es nuestro.
              </AccordionItem>
              <AccordionItem title="¿El acceso vence?" data-id="element-346">
                No. El acceso es de por vida e incluye las actualizaciones
                futuras del material sin costo adicional.
              </AccordionItem>
            </div>
          </div>
        </section>

        {/* SECTION 14 — FINAL CTA */}
        <section className="bg-[#0A0A0A] py-12 md:py-20 px-4" data-id="element-347">
          <div className="max-w-xl mx-auto" data-id="element-348">
            <div className="bg-[#161616] border border-white/10 rounded-3xl p-6 md:p-10" data-id="element-349">
              <div className="text-center" data-id="element-350">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 uppercase tracking-wide" data-id="element-351">
                  NO DEJES PASAR ESTA OPORTUNIDAD
                </h2>

                <p className="text-gray-400 text-sm tracking-widest uppercase mb-4" data-id="element-352">
                  TERMINA EN
                </p>

                <div className="mb-8" data-id="element-353">
                  <CountdownTimer data-id="element-354" />
                </div>
              </div>

              {/* Pricing Options — Compact Side-by-Side */}
              <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-355">
                {/* Option 1: Técnico */}
                <button onClick={() => setSelectedPlan('tecnico')} className={`text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-300 flex flex-col h-full ${selectedPlan === 'tecnico' ? 'border-[#F4820A] bg-[#F4820A]/5' : 'border-white/10 bg-white/5 hover:bg-white/10'}`} data-id="element-356">
                  <div className="flex items-center gap-2 mb-2" data-id="element-357">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'tecnico' ? 'border-[#F4820A]' : 'border-gray-500'}`} data-id="element-358">
                      {selectedPlan === 'tecnico' && <div className="w-2 h-2 rounded-full bg-[#F4820A]" data-id="element-359"></div>}
                    </div>
                    <FileText size={14} className={selectedPlan === 'tecnico' ? 'text-[#F4820A]' : 'text-gray-400'} data-id="element-360" />
                  </div>
                  <h4 className="text-white font-bold text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-361">
                    KIT TÉCNICO
                  </h4>
                  <p className="text-gray-400 text-[10px] md:text-xs mt-1 mb-2" data-id="element-362">
                    3 Tomos + 3 Bonos
                  </p>
                  <div className="mt-auto" data-id="element-363">
                    <span className="text-gray-500 line-through text-[10px] md:text-xs block mb-0.5" data-id="element-364">
                      $31.970
                    </span>
                    <span className="text-white font-bold text-base md:text-lg" data-id="element-365">
                      $19.990
                    </span>
                  </div>
                </button>

                {/* Option 2: Completo */}
                <button onClick={() => setSelectedPlan('completo')} className={`text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-300 flex flex-col h-full relative ${selectedPlan === 'completo' ? 'border-[#F4820A] bg-[#F4820A]/5' : 'border-white/10 bg-white/5 hover:bg-white/10'}`} data-id="element-366">
                  <div className="flex items-center gap-2 mb-2" data-id="element-367">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedPlan === 'completo' ? 'border-[#F4820A]' : 'border-gray-500'}`} data-id="element-368">
                      {selectedPlan === 'completo' && <div className="w-2 h-2 rounded-full bg-[#F4820A]" data-id="element-369"></div>}
                    </div>
                    <Star size={14} className={selectedPlan === 'completo' ? 'text-[#F4820A] fill-[#F4820A]' : 'text-gray-400'} data-id="element-370" />
                  </div>
                  <h4 className="text-white font-bold text-sm md:text-base uppercase tracking-wide leading-tight" data-id="element-371">
                    KIT COMPLETO
                  </h4>
                  <div className="flex flex-wrap items-center gap-1 mt-1 mb-2" data-id="element-372">
                    <span className="bg-[#F4820A] text-white text-[8px] md:text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-373">
                      ⭐ MÁS ELEGIDO
                    </span>
                  </div>
                  <p className="text-gray-400 text-[10px] md:text-xs mb-2" data-id="element-374">
                    6 Tomos + 3 Bonos
                  </p>
                  <div className="mt-auto" data-id="element-375">
                    <span className="text-gray-500 line-through text-[10px] md:text-xs block mb-0.5" data-id="element-376">
                      $59.940
                    </span>
                    <span className="text-[#F4820A] font-bold text-lg md:text-xl" data-id="element-377">
                      $29.990
                    </span>
                  </div>
                </button>
              </div>

              {/* CTA Button */}
              <a href={checkoutLink} className="w-full bg-[#F4820A] hover:bg-[#d97409] text-white font-bold text-lg md:text-xl py-3 md:py-4 rounded-xl transition-colors shadow-[0_0_30px_rgba(244,130,10,0.2)] mb-4 flex items-center justify-center gap-2" data-id="element-378">
                {selectedPlan === 'completo' ? 'SÍ, QUIERO EL KIT COMPLETO' : 'SÍ, QUIERO EL KIT TÉCNICO'}{' '}
                <span className="text-2xl leading-none" data-id="element-379">→</span>
              </a>

              <p className="text-gray-400 text-xs md:text-sm text-center mb-8 italic" data-id="element-380">
                La opción más elegida por gasistas que quieren el sistema
                completo de oficio y negocio
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-400 font-medium" data-id="element-381">
                <div className="flex items-center gap-1.5" data-id="element-382">
                  <Lock size={16} className="text-[#F4820A]" data-id="element-383" /> Pago seguro
                </div>
                <div className="flex items-center gap-1.5" data-id="element-384">
                  <Zap size={16} className="text-[#F4820A]" data-id="element-385" /> Acceso en 5 min
                </div>
                <div className="flex items-center gap-1.5" data-id="element-386">
                  <ShieldCheck size={16} className="text-[#F4820A]" data-id="element-387" /> Garantía
                  7 días
                </div>
                <div className="flex items-center gap-1.5" data-id="element-388">
                  <CreditCard size={16} className="text-[#F4820A]" data-id="element-389" /> Mercado
                  Pago
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="bg-[#0A0A0A] border-t border-white/10 py-10 px-4" data-id="element-390">
          <div className="max-w-3xl mx-auto text-center" data-id="element-391">
            <p className="text-xl md:text-2xl font-extrabold text-white tracking-wide" data-id="element-392">
              idea<span className="text-[#F4820A]" data-id="element-393">hub</span>digital
            </p>
            <a href="mailto:ideahubdigital.7@gmail.com" className="inline-block mt-3 text-gray-400 hover:text-[#F4820A] text-sm md:text-base transition-colors" data-id="element-394">
              ideahubdigital.7@gmail.com
            </a>
            <p className="text-gray-500 text-xs md:text-sm mt-4" data-id="element-395">
              © {new Date().getFullYear()} ideahubdigital. Todos los derechos
              reservados.
            </p>
          </div>
        </footer>
      </div>
    </>;
}